import {PolymerElement, html} from '@polymer/polymer';
import '@polymer/polymer/lib/elements/dom-if.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {InputBehavior} from '../behaviors/input-behavior.js';
import {DropdownBehavior} from '../behaviors/dropdown-behavior.js';
import {InputSharedStylesHTML} from '../shared-styles/input.js';
import {PikadaySharedStyles} from '../shared-styles/pikaday.js';
import {PikadayCustomizationStyles} from '../shared-styles/pikaday-customizations.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {format, getHours, subHours, addHours, subDays, addDays, startOfMonth, lastDayOfMonth, isValid} from 'date-fns';
import {parseDateAndTime, parseTime} from '@beauwest/business-date-parser';

class KeliDateTime extends ScopeBinding(HotkeyBehavior(DropdownBehavior(InputBehavior(PolymerElement)))) {
  static get template() {
    return html`
${InputSharedStylesHTML}
<style>
#fieldContainer {
  display: flex;
}
#date {
  font-size: var(--keli-input-field-font-size, .93em);
  letter-spacing: -1.5px;
  background-color: var(--keli-subtle-background);
  padding: 0 3px 0 3px;
  margin-right: 3px;
  border: 1px solid var(--keli-subtle-border-color);
  box-sizing: border-box;
  border-bottom: 0;
  white-space: nowrap;
  color: var(--keli-text);
}

#input {
  padding-right: 20px !important;
}
</style>

<keli-input-container id="container"
    label="[[caption]]"
    input-has-content="[[active]]"
    icon="date-range"
    always-float-label="[[masked]]"
    focused="[[focused]]"
    disabled="[[disabled]]"
    invalid="[[invalid]]"
    error-message="[[errorMessage]]"
    hotkey="[[formattedShortcutKey]]"
    chrome="[[chrome]]"
    help-text="[[helpText]]">

  <div id="fieldContainer" slot="input">
    <template is="dom-if" if="[[dateTime]]">
      <div id="date">[[date]]</div>
    </template>
    <input type="text" id="input">
  </div>
  <div slot="postscript">[[willParseTo]]</div>
</keli-input-container>`;
  }

  static get is() {
    return 'keli-datetime';
  }

  static get properties() {
    return {
      // @formatter:off
      date: {type: String, computed: 'computeDate(dateTime)'},
      dataFormat: {type: String, value: 'yyyy-MM-dd HH:mm:ss'},
      dateTime: {type: Object, computed: 'computeDateTime(currentValue)', observer: 'dateTimeChanged'},
      dateWireFormat: {type: String, value: 'M/d/y'},
      defaultDate: {type: String, scopevar: true},
      inputFormatRequired: {type: Boolean, value: true},
      keliControlTypes: {type: Array, value: ['datetime']},
      military: {type: Boolean, scopevar: true},
      seconds: {type: Boolean, scopevar: true},
      timeWireFormat: {type: String, computed: 'computeTimeWireFormat(seconds, military, computeWireFormatInitialization)', observer: 'timeWireFormatChanged'},
      willParseTo: String,
      willParseToFormat: {type: String, computed: 'computeWillParseToFormat(dateTime, timeWireFormat, computeWireFormatInitialization)'},
      wireFormat: {type: String, computed: 'computeWireFormat(dateWireFormat, timeWireFormat, computeWireFormatInitialization)'}
      // @formatter:on
    }
  }

  get dropdownBehaviorStyles() {
    return `
${PikadaySharedStyles.cssText}
${PikadayCustomizationStyles.cssText}
`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.computeWireFormatInitialization = true;
    this._hotkeyListeners[this._defaultListenerName].stop_listening();

    this.dropdownElement.positionElement = this.$.container;
    this.addEventListener('keli-input-icon-activated', this.toggleDatePicker);

    // Create the Date picker
    this.datePickerInstance = new Pikaday({
      keyboardInput: false,
      bound: false,
      reposition: false,
      defaultDate: this.dateTime || new Date(),
      setDefaultDate: false,
      yearRange: 10,
      onSelect: selectedDate => {
        if (this.datePickerInstance && this.dropdownElement.visible()) {
          if (this.dateTime) {
            selectedDate.setHours(this.dateTime.getHours());
            selectedDate.setMinutes(this.dateTime.getMinutes());
            selectedDate.setSeconds((this.seconds ? this.dateTime.getSeconds() : 0));
          }
          this.setFromHotkey(this.toWireFormat(selectedDate));
          this.toggleDatePicker();
          this.$.input.focus();
        }
      },
      theme: `keli-${KeliInterface.Styles.palette.darkMode ? 'dark' : 'light'}`
    });
    this.dropdownElement.addContentElement(this.datePickerInstance.el);

    this.registerHotkey(KeliInterface.Hotkeys.time.clear, {
      down: () => {
        this.setFromHotkey(null);
      }
    });

    this.registerHotkey(KeliInterface.Hotkeys.time.current, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(new Date()));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.time.am, {
      down: () => {
        if (this.dateTime) {
          let parsed = this.mergeDateAndTime(this.dateTime, parseTime(`${this.$.input.value} AM`));
          if (parsed) {
            let hours = getHours(parsed);
            if (hours >= 12) {
              this.setFromHotkey(this.toWireFormat(subHours(parsed, 12)));
            } else {
              this.setFromHotkey(this.toWireFormat(parsed));
            }
          }
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.time.pm, {
      down: () => {
        if (this.dateTime) {
          let parsed = this.mergeDateAndTime(this.dateTime, parseTime(`${this.$.input.value} PM`));
          if (parsed) {
            let hours = getHours(parsed);
            if (hours < 12) {
              this.setFromHotkey(this.toWireFormat(addHours(parsed, 12)));
            } else {
              this.setFromHotkey(this.toWireFormat(parsed));
            }
          }
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.date.yesterday, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(subDays(new Date(), 1)));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.date.tomorrow, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(addDays(new Date(), 1)));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.date.first, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(startOfMonth(new Date())));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.date.last, {
      down: () => {
        this.setFromHotkey(this.toWireFormat(lastDayOfMonth(new Date())));
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.date.toggleCalendar, {
      down: () => {
        this.toggleDatePicker();
      }
    });

    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.registerHotkey(KeliInterface.Hotkeys.date.hide, {
      down: event => {
        if (this.dropdownElement && this.dropdownElement.visible()) {
          event.preventDefault();
          event.stopPropagation();
          this.toggleDatePicker();
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.date.add, {
      down: event => {
        if (this.dateTime) {
          event.preventDefault();
          event.stopPropagation();
          this.setFromHotkey(this.toWireFormat(addDays(this.dateTime, 1)));
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.date.subtract, {
      down: event => {
        if (this.dateTime) {
          event.preventDefault();
          event.stopPropagation();
          this.setFromHotkey(this.toWireFormat(subDays(this.dateTime, 1)));
        }
      }
    }, 'raw-listener');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keli-input-icon-activated', this.toggleDatePicker);

    if (this.datePickerInstance) {
      this.datePickerInstance.destroy();
      this.datePickerInstance = null;
    }
  }

  computeTimeWireFormat(seconds, military) {
    let parts = [
      (military ? 'HH' : 'h'),
      'mm'
    ];
    if (seconds) {
      parts.push('ss');
    }
    let suffix = (military ? '' : ' a');
    return `${parts.join(':')}${suffix}`;
  }

  timeWireFormatChanged(newValue) {
    if (this.dateTime) {
      this.$.input.value = this.toWireFormat(this.$.input.value, newValue);
    }
  }

  computeWillParseToFormat(dateTime, timeWireFormat) {
    let date = (dateTime ? '' : `${this.dateWireFormat} `);
    return `${date}${timeWireFormat}`;
  }

  computeWireFormat(dateWireFormat, timeWireFormat) {
    if (!timeWireFormat) {
      timeWireFormat = this.computeTimeWireFormat(this.seconds, this.military);
    }
    return `${dateWireFormat} ${timeWireFormat}`;
  }

  toggleDatePicker() {
    if (this.readOnly) {
      return;
    }
    if (this.datePickerInstance) {
      if (this.dropdownElement.visible()) {
        this.dropdownElement.hide();
      } else {
        if (this.dateTime) {
          this.datePickerInstance.setDate(this.dateTime, true);
        } else {
          this.datePickerInstance.gotoToday();
        }
        this.dropdownElement.show();
      }
    }
  }

  computeDate(dateTime) {
    if (!dateTime) {
      return undefined;
    }
    return format(dateTime, this.dateWireFormat);
  }

  computeDateTime(currentValue) {
    if (!currentValue) {
      return undefined;
    }
    return parseDateAndTime(currentValue);
  }

  dateTimeChanged(newValue) {
    this.$.input.value = (newValue ? this.toWireFormat(newValue, this.timeWireFormat) : '');
    if (newValue) {
      this._hotkeyListeners && this._hotkeyListeners[this._defaultListenerName].listen();
    } else {
      this._hotkeyListeners && this._hotkeyListeners[this._defaultListenerName].stop_listening();
    }
  }

  setFromHotkey(newValue) {
    if (newValue === this.currentValue) {
      this.currentValueChanged(newValue);
    } else {
      this.currentValue = newValue;
    }
    this.fireKeliAccepted = true;
    this.willParseTo = null;
    this._handleInput();
  }

  toWireFormat(newValue, wireFormat) {
    if (!newValue) {
      return null;
    }

    // Manually compute the wire format if it's not set up yet.
    if (!wireFormat && (!this.wireFormat || !this.dateWireFormat || !this.timeWireFormat)) {
      wireFormat = this.computeWireFormat(this.dateWireFormat, this.timeWireFormat);
    }

    let parsed = parseDateAndTime(newValue);
    return (isValid(parsed) ? format(parsed, wireFormat || this.wireFormat) : undefined);
  }

  mergeDateAndTime(dateValue, timeValue) {
    if (!dateValue || !dateValue instanceof Date) {
      return undefined;
    }
    if (!timeValue || !timeValue instanceof Date) {
      return undefined;
    }
    let final = new Date(dateValue.getTime());
    final.setHours(timeValue.getHours());
    final.setMinutes(timeValue.getMinutes());
    final.setSeconds(timeValue.getSeconds());
    final.setMilliseconds(timeValue.getMilliseconds());
    return final;
  }

  displayValueChanged(newValue) {
    if (newValue) {
      return this.toWireFormat(newValue);
    }
  }

  toRawFieldValue(value) {
    return this.toWireFormat(value, this.dataFormat);
  }

  currentValueChanged(newValue) {
    if (this.toRawFieldValue(this.displayValue) !== this.toRawFieldValue(newValue)) {
      this.$.input.value = (newValue ? this.toWireFormat(newValue) : '');
    }
    this.active = (this.$.input.value || this.dateTime);

    if (this.datePickerInstance) {
      this.datePickerInstance.setDate(this.dateTime || new Date(), true);
    }
  }

  handleInput() {
    this.active = (this.$.input.value || this.dateTime);

    let parsedDisplayValue = '';
    if (this.dateTime) {
      let parsed = this.mergeDateAndTime(this.dateTime, parseTime(this.$.input.value));
      parsedDisplayValue = parsed && format(parsed, this.willParseToFormat);
    } else if (this.$.input.value) {
      let parsed = parseDateAndTime(this.$.input.value, {preferTime: true, defaultDate: this.defaultDate});
      parsedDisplayValue = format(parsed, this.willParseToFormat);
    }
    if (parsedDisplayValue && this.$.input.value && this.$.input.value !== parsedDisplayValue) {
      this.willParseTo = `Will parse to: ${parsedDisplayValue}`;
    } else {
      this.willParseTo = '';
    }
  }

  handleFocus() {
    this.resetHotkeyState();
  }

  keliAccept() {
    let parsed;
    if (this.dateTime) {
      parsed = this.mergeDateAndTime(this.dateTime, parseTime(this.$.input.value));
    } else {
      parsed = parseDateAndTime(this.$.input.value, {preferTime: true, defaultDate: this.defaultDate});
    }
    if (parsed && isValid(parsed)) {
      this.currentValue = this.toWireFormat(parsed);
    }
    this.willParseTo = '';
  }
}

customElements.define(KeliDateTime.is, KeliDateTime);
export {KeliDateTime};
